import { generatePublicOrgToken } from '@easy-expense/auth-client';
import {
  useWorkspaceStore,
  useWorkspaceKeysStore,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { StatementTransactionSchema } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';
import Papa from 'papaparse';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { FailedStatementParsingModal } from './FailedStatementParsingModal.component';
import { ReconcileModal } from './ReconcileModal.component';
import { autoMatchColumns } from './autoMatchColumns';
import {
  DateOrder,
  dateOrderFromFormat,
  detectDateOrder,
  isValidStatementDate,
  parseStatementDate,
} from './dateParsing';
import { reconcileStatement, ReconciledStatementRow } from './reconcile';
import LoadingSpinner from '../LoadingSpinner.component';

function validateStatementsModuleAvailability(): boolean {
  if (!Data.statements) {
    console.error(
      'Data.statements is not available. Make sure the Statements module is properly initialized.',
    );
    alert(getTranslation('Error: Statements module is not initialized. Please refresh the page.'));
    return false;
  }
  return true;
}

function validateWorkspaceKeys(
  organizationID: string | undefined,
  workspaceID: string | undefined,
): boolean {
  if (!organizationID || !workspaceID) {
    console.error('Missing workspace or organization keys:', {
      organizationID,
      workspaceID,
    });
    alert(getTranslation('Error: Missing workspace information. Please refresh the page.'));
    return false;
  }
  return true;
}

function parseTotalValue(totalValue: unknown): number {
  if (typeof totalValue === 'number') {
    return Math.abs(totalValue);
  }
  if (totalValue !== undefined && totalValue !== null) {
    const cleaned = String(totalValue)
      .replace(/[$€£¥₹₦₨₩₪₫₱₴₽₸₺₼₾₿R,()]/g, '')
      .trim();
    return Math.abs(parseFloat(cleaned) || 0);
  }
  return 0;
}

function transformStatementDataToTransactions(
  statementData: DoNotFixMeIAmAny[],
  dateHeader: string,
  totalHeader: string,
  statementDescriptorHeader: string,
  dateOrder: DateOrder,
) {
  return statementData.map((row: DoNotFixMeIAmAny) => {
    const dateValue = row[dateHeader];
    const totalValue = row[totalHeader];
    const description = statementDescriptorHeader
      ? String(row[statementDescriptorHeader] || '')
      : undefined;

    const dateString = parseStatementDate(dateValue, dateOrder);
    const total = parseTotalValue(totalValue);

    return StatementTransactionSchema.parse({
      date: dateString,
      total,
      ...(description && description.trim() && { description: description.trim() }),
    });
  });
}

function calculateDateRange(transactions: { date: string }[]): { min: string; max: string } {
  const sortedTransactions = transactions.sort((a, b) => (a.date < b.date ? -1 : 1));
  const minDate = sortedTransactions[0]?.date ?? '2000-01-01';
  const maxDate = sortedTransactions[sortedTransactions.length - 1]?.date ?? '2000-01-01';

  return { min: minDate, max: maxDate };
}

function calculateReconciliationStats(reconciledStatement: ReconciledStatementRow[]): {
  reconciledCount: number;
  missingCount: number;
} {
  const reconciledCount = reconciledStatement.filter((row) => row.Status === 'Reconciled').length;
  const missingCount = reconciledStatement.filter((row) => row.Status === 'Missing').length;

  return { reconciledCount, missingCount };
}

function handleStatementCreationSuccess(
  statementKey: string,
  organizationID: string,
  workspaceID: string,
  navigate: (path: string) => void,
) {
  console.log('Statement created successfully with key:', statementKey);
  console.log(
    'Statement should be saved to:',
    `organizations/${organizationID}/workspaces/${workspaceID}/statements/${statementKey}`,
  );
  navigate(`/statement/${statementKey}`);
}

function handleStatementCreationFailure() {
  console.error('Statement creation returned no key - check console for errors');
  alert(getTranslation('Error: Statement creation failed. Please check the console for details.'));
}

function handleStatementCreationError(
  error: unknown,
  statementName: string,
  transactionCount: number,
  dateRange: { min: string; max: string },
  organizationID: string | undefined,
  workspaceID: string | undefined,
) {
  console.error('Error saving statement:', error);
  console.error('Error details:', {
    error,
    errorMessage: error instanceof Error ? error.message : String(error),
    errorStack: error instanceof Error ? error.stack : undefined,
    statementName,
    transactionCount,
    dateRange,
    organizationID,
    workspaceID,
  });
  alert(
    getTranslation('Error saving statement. Please check the console for details and try again.'),
  );
}

export const ReconcileFileUpload: React.FC<React.PropsWithChildren<object>> = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [dateHeader, setDateHeader] = React.useState('');
  const [totalHeader, setTotalHeader] = React.useState('');
  const [statementDescriptorHeader, setStatementDescriptorHeader] = React.useState<string>('');
  const [showModal, setShowModal] = React.useState(false);
  const [showFailedParseModal, setShowFailedParseModal] = React.useState(false);
  const [statementFile, setStatementFile] = React.useState<File | null>(null);
  const [statementName, setStatementName] = React.useState<string>('');
  const [assignedTo, setAssignedTo] = React.useState<string[]>([]);
  const workspaceMembers = useWorkspaceStore((s) => s.workspaceMembers);
  const { workspaceSettings } = useWorkspaceSettings();

  const [token, setToken] = React.useState('');
  const { organizationID, workspaceID } = useWorkspaceKeysStore((s) => ({
    workspaceID: s.currentWorkspaceKey,
    organizationID: s.currentOrganizationKey,
  }));

  React.useEffect(() => {
    async function getToken() {
      if (organizationID && workspaceID) {
        const tokenResponse = await generatePublicOrgToken()({
          organizationID,
          workspaceID,
        });
        setToken(encodeURIComponent(tokenResponse.data));
      }
    }
    if (!token) {
      getToken();
    }
  }, [organizationID, workspaceID, token]);

  async function onUpload(files?: FileList | null) {
    setIsLoading(true);
    if (!files || files.length === 0) {
      setIsLoading(false);
      return null;
    }

    const file = files[0];

    if (!file || (file.type !== 'text/csv' && !file.name.endsWith('.csv'))) {
      alert(getTranslation('Please upload a CSV file'));
      setIsLoading(false);
      return;
    }

    setStatementFile(file);
    // Set default statement name from filename (without .csv extension)
    const defaultName = file.name.replace(/\.csv$/i, '');
    setStatementName(defaultName);

    Papa.parse(file, {
      header: false, // Don't transform into objects
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          alert(getTranslation('Error parsing credit card statement'));
          console.error('ERROR parsing CSV ', results.errors);
          setIsLoading(false);
          return;
        }

        // Get headers from first row
        const csvHeaders = results.data[0] as string[];

        // Convert raw CSV rows (arrays) into objects keyed by headers for analysis
        // Use up to 20 sample rows for auto-matching (or all rows if fewer than 20)
        const sampleRows = (results.data.slice(1, 21) as string[][]).map((row: string[]) => {
          const rowObj: DoNotFixMeIAmAny = {};
          csvHeaders.forEach((header, index) => {
            rowObj[header] = row[index];
          });
          return rowObj;
        });

        // Auto-match date and total columns using smart detection
        const {
          dateHeader: matchedDateHeader,
          totalHeader: matchedTotalHeader,
          statementDescriptorHeader: matchedStatementDescriptorHeader,
        } = autoMatchColumns(csvHeaders, sampleRows);

        if (matchedDateHeader) {
          setDateHeader(matchedDateHeader);
        }
        if (matchedTotalHeader) {
          setTotalHeader(matchedTotalHeader);
        }
        if (matchedStatementDescriptorHeader) {
          setStatementDescriptorHeader(matchedStatementDescriptorHeader);
        }

        setHeaders(csvHeaders);
        setShowModal(true);
        setIsLoading(false);
      },
      error: (error) => {
        alert(getTranslation('Error parsing credit card statement'));
        console.error('ERROR parsing CSV error:', error);
        setIsLoading(false);
      },
    });
  }

  async function onReconcile() {
    if (!statementFile) {
      console.error('Error no statement file');
      return;
    }
    Papa.parse(statementFile as File, {
      header: true,
      skipEmptyLines: true,
      error: (error) => {
        alert(getTranslation('Error parsing credit card statement'));
        console.error('ERROR parsing CSV error:', error);
        setIsLoading(false);
      },
      complete: async (results) => {
        setShowModal(false);
        setIsLoading(true);

        // Resolve mm/dd vs dd/mm once for the whole file so date validation and
        // parsing stay consistent, disambiguating with the workspace date format
        // setting when the data itself is ambiguous (EZ-4027).
        const dateOrder = detectDateOrder(
          results.data.map((s: DoNotFixMeIAmAny) => s[dateHeader]),
          dateOrderFromFormat(workspaceSettings?.dateFormat),
        );

        const hasInvalidDates = results.data.some(
          (s: DoNotFixMeIAmAny) => !isValidStatementDate(s[dateHeader] as string, dateOrder),
        );
        const hasInvalidTotals = results.data.some((s: DoNotFixMeIAmAny) =>
          isNaN(Number(s[totalHeader])),
        );

        if (hasInvalidTotals || hasInvalidDates) {
          setShowFailedParseModal(true);
          setIsLoading(false);
          return;
        }

        const result = await reconcileStatement({
          statementData: results.data,
          workspaceMembers,
          totalHeader,
          dateHeader,
          token,
          showTax: !!(
            workspaceSettings?.taxRates && workspaceSettings.taxRates.find((t) => t.tracked)
          ),
          dateFormat: workspaceSettings?.dateFormat,
        });

        if (!validateStatementsModuleAvailability()) {
          setIsLoading(false);
          return;
        }

        if (!validateWorkspaceKeys(organizationID, workspaceID)) {
          setIsLoading(false);
          return;
        }

        let transactions: any[] = [];
        let minDate = '';
        let maxDate = '';

        try {
          transactions = transformStatementDataToTransactions(
            results.data,
            dateHeader,
            totalHeader,
            statementDescriptorHeader,
            dateOrder,
          );

          const dateRange = calculateDateRange(transactions);
          minDate = dateRange.min;
          maxDate = dateRange.max;

          const { reconciledCount, missingCount } = calculateReconciliationStats(
            result.reconciledStatement,
          );

          const statementKey = Data.statements.create(
            {
              name: statementName || statementFile.name.replace(/\.csv$/i, ''),
              assignedTo: assignedTo.length > 0 ? assignedTo : [],
              dateHeader,
              totalHeader,
              statementDescriptorHeader: statementDescriptorHeader || null,
              transactions,
              dateRange: {
                min: minDate,
                max: maxDate,
              },
              reconciledCount,
              missingCount,
              noReceiptCount: 0,
            },
            organizationID,
            workspaceID,
          );

          if (statementKey) {
            handleStatementCreationSuccess(statementKey, organizationID, workspaceID, navigate);
          } else {
            handleStatementCreationFailure();
          }
        } catch (error) {
          handleStatementCreationError(
            error,
            statementName,
            transactions.length,
            { min: minDate, max: maxDate },
            organizationID,
            workspaceID,
          );
        }

        setIsLoading(false);
      },
    });
  }

  if (isLoading) {
    return (
      <Layout.Column center grow>
        <Layout.Row center>
          <LoadingSpinner />
        </Layout.Row>
      </Layout.Column>
    );
  }

  return (
    <Layout.Column
      onDrop={(event: DragEvent) => {
        event.preventDefault();
        onUpload(event?.dataTransfer?.files);
      }}
      onDragOver={(event: DragEvent) => event.preventDefault()}
    >
      <Layout.PressableColumn bg="transparent">
        <label
          htmlFor="file"
          style={{
            borderStyle: 'dashed',
            borderWidth: 1,
            borderRadius: 6,
            borderColor: theme.colors.brandPrimary,
            backgroundColor: theme.colors.brandPrimaryXLight,
            padding: 32,
            display: 'flex',
            cursor: 'pointer',
            width: 'fit-content',
          }}
        >
          <Layout.Column style={{ justifyContent: 'space-between' }}>
            <Layout.Row align>
              <Icon name="receipt-outline" size={23} color={theme.colors.brandPrimary} />
              <Spacer.Horizontal size={12} />
              <Layout.Column>
                <OpenSans.Pressable weight="bold-700">
                  {getTranslation('Drag & drop credit card statement')}
                </OpenSans.Pressable>
                <Spacer.Vertical size={4} />
                <OpenSans.Pressable size="xs-12">
                  {getTranslation('We will reconcile automatically')}
                </OpenSans.Pressable>
              </Layout.Column>
            </Layout.Row>
            <input
              type="file"
              id="file"
              accept=".csv"
              onChange={(event) => {
                onUpload(event.target.files);
                event.target.value = ''; // so that you can re-upload the same file
              }}
              style={{ display: 'none' }}
            />
          </Layout.Column>
        </label>
      </Layout.PressableColumn>

      <ReconcileModal
        showModal={showModal}
        setShowModal={setShowModal}
        onReconcile={onReconcile}
        dateHeader={dateHeader}
        setDateHeader={setDateHeader}
        totalHeader={totalHeader}
        setTotalHeader={setTotalHeader}
        headers={headers}
        statementName={statementName}
        setStatementName={setStatementName}
        assignedTo={assignedTo}
        setAssignedTo={setAssignedTo}
      />
      <FailedStatementParsingModal
        showModal={showFailedParseModal}
        setShowModal={setShowFailedParseModal}
      />
    </Layout.Column>
  );
};
